import ApiServiceBase from "@/services/api/apiServiceBase";
import {ChatInterface} from "@/interfaces/ChatInterface";
import {ChatMessageInterface} from "@/interfaces/ChatMessageInterface";
import {Preferences} from "@capacitor/preferences";
import {PreferenceKey} from "@/definitions/PreferenceKey";
import {UnseenMessagesInterface} from "@/interfaces/UnseenMessagesInterface";
import {dataUrlToBlob, normalizePaginated} from "@/services/api/normalizeProfile";
import {normalizeChat, normalizeChatMessages, normalizeChats} from "@/services/api/normalizeChat";

export class ChatService extends ApiServiceBase {

    public async get(page = 1, limit = 10): Promise<ChatInterface[]> {
        const payload = await this.http?.get('/chats', {page, limit});
        return await normalizeChats(normalizePaginated(payload).data);
    }

    public async getById(chatId: number): Promise<any> {
        // No dedicated GET /chats/:id — resolve from inbox / archived lists
        const inbox = await this.get(1, 50);
        const found = inbox.find((c: any) => Number(c.id) === Number(chatId));
        if (found) return found;
        try {
            const archived = await this.getArchived();
            return archived.find((c: any) => Number(c.id) === Number(chatId)) ?? null;
        } catch {
            return null;
        }
    }

    public async with(userId: number): Promise<ChatInterface | null> {
        try {
            const raw = await this.http?.post('/chats/single', {userId: Number(userId)});
            // Contract: null when no thread exists yet (first message uses peerUserId on socket)
            if (!raw) return null;
            return normalizeChat(raw);
        } catch (e: any) {
            const msg = String(e?.message || e?.error || '').toLowerCase();
            if (msg.includes('conversation not found') || msg.includes('chat not found') || msg.includes('not found')) {
                return null;
            }
            throw e;
        }
    }

    public async getArchived(): Promise<ChatInterface[]> {
        const payload = await this.http?.get('/chats', {page: 1, limit: 50, archived: true});
        return await normalizeChats(normalizePaginated(payload).data);
    }

    /**
     * Messages are sent over Socket.IO (`send_message`), not HTTP.
     * @see API_PWA.md — Chat (Socket.IO)
     */
    public async postMessage(_chatId: number, _message: string, _media?: string | null): Promise<ChatMessageInterface> {
        return Promise.reject({
            success: false,
            message: 'Chat messages must be sent via Socket.IO send_message, not HTTP',
            error: 'USE_SOCKET',
        });
    }

    /** Alias kept for callers that still name this sendMessage. */
    public async sendMessage(_chatId: number, _message: string, _media?: string | null): Promise<ChatMessageInterface> {
        return this.postMessage(_chatId, _message, _media);
    }

    public async deleteMessage(messageId: number): Promise<any> {
        return this.http?.delete(`/chats/messages/${messageId}`, {});
    }

    public async conversation(chatId: number, page = 1, limit = 50): Promise<any> {
        try {
            const payload = await this.http?.get(`/chats/messages/${chatId}`, {page, limit});
            const {data, pagination} = normalizePaginated(payload);
            const messages = await normalizeChatMessages(data);
            return {
                data: messages,
                meta: {
                    current_page: pagination.page ?? page,
                    last_page: pagination.totalPages ?? 1,
                    per_page: pagination.limit ?? limit,
                    total: pagination.total ?? messages.length,
                },
                pagination,
            };
        } catch (e: any) {
            const msg = String(e?.message || e?.error || '').toLowerCase();
            if (msg.includes('conversation not found') || msg.includes('chat not found') || msg.includes('not found')) {
                return {
                    data: [],
                    meta: {current_page: 1, last_page: 1, per_page: limit, total: 0},
                    pagination: {page: 1, limit, total: 0, totalPages: 0},
                };
            }
            throw e;
        }
    }

    public async create(name: string, participants: number[], type: 'private' | 'group' = 'group'): Promise<ChatInterface> {
        const form = new FormData();
        form.append('name', name);
        form.append('type', type);
        form.append('participants', JSON.stringify(participants.map(Number)));
        return normalizeChat(await this.http?.upload('/chats', form));
    }

    public async isActive(chatId: number, value: boolean): Promise<any> {
        // No server "active chat" route — keep local preference only
        if (value) {
            await Preferences.set({key: PreferenceKey.ActiveChatId, value: `${chatId}`});
        } else {
            await Preferences.remove({key: PreferenceKey.ActiveChatId});
        }
        return {chatId, active: value};
    }

    public async delete(chatId: number): Promise<any> {
        return this.http?.post('/chats/delete', {chatId: Number(chatId)});
    }

    public async archive(chatId: number): Promise<any> {
        return this.http?.post('/chats/archive', {chatId: Number(chatId)});
    }

    public async leave(chatId: number): Promise<any> {
        return this.http?.post('/chats/leave', {chatId: Number(chatId)});
    }

    public async unarchive(chatId: number): Promise<any> {
        return this.http?.post('/chats/unarchive', {chatId: Number(chatId)});
    }

    public async mute(chatId: number): Promise<any> {
        return this.http?.post('/chats/mute', {chatId: Number(chatId)});
    }

    public async unmute(chatId: number): Promise<any> {
        return this.http?.post('/chats/unmute', {chatId: Number(chatId)});
    }

    public async pin(chatId: number): Promise<any> {
        return this.http?.post('/chats/pin', {chatId: Number(chatId)});
    }

    public async unpin(chatId: number): Promise<any> {
        return this.http?.post('/chats/unpin', {chatId: Number(chatId)});
    }

    public async clear(chatId: number): Promise<any> {
        return this.http?.post('/chats/clear', {chatId: Number(chatId)});
    }

    public async unseenMessages(): Promise<UnseenMessagesInterface> {
        // No dedicated unseen endpoint — derive a stub count from inbox if needed
        try {
            const chats = await this.get(1, 50);
            const messages = chats.reduce((sum: number, chat: any) => {
                return sum + Number(chat.unseenCount ?? chat.unseen ?? chat.unreadCount ?? 0);
            }, 0);
            return {messages} as UnseenMessagesInterface;
        } catch {
            return {messages: 0} as UnseenMessagesInterface;
        }
    }

    public async setImage(chatId: number, base64Image: string): Promise<ChatInterface> {
        const form = new FormData();
        const blob = await dataUrlToBlob(base64Image);
        form.append('photo', blob, 'chat.jpg');
        return normalizeChat(await this.http?.upload(`/chats/${chatId}`, form, undefined, 'PATCH'));
    }

    public async addParticipants(chatId: number, participantIds: number[]): Promise<any> {
        return this.http?.post(`/chats/${chatId}/participants`, {participantIds: participantIds.map(Number)});
    }

    public async shareEvent(chatId: number, eventId: number, comment?: string): Promise<any> {
        const body: Record<string, any> = {eventId: Number(eventId)};
        if (comment) body.comment = comment;
        return this.http?.post(`/chats/${chatId}/share-event`, body);
    }

    public async markRead(messageId: number): Promise<any> {
        return this.http?.post(`/chats/messages/${messageId}/read`, {});
    }
}
